import { useQuery } from "@apollo/client";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import AmericanCalendarContent from "components/company/v3/american/contents/events/AmericanCalendarContent";
import AmericanDividendsContent from "components/company/v3/american/contents/events/AmericanDividendsContent";
import AmericanRightsIssuesContent from "components/company/v3/american/contents/events/AmericanRightsIssuesContent";
import AmericanStockDividendsContent from "components/company/v3/american/contents/events/AmericanStockDividendsContent";
import AmericanStockSplitContent from "components/company/v3/american/contents/events/AmericanStockSplitContent";
import useAmericanIssuer from "components/company/v3/context/hooks/useAmericanIssuer";
import { CompanyEventsTabEnum } from "components/company/v3/menu/enums";
import type { EventsCalendarState } from "components/investorrelations/EventsCalendarLocalState";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import AmericanIssuerDividendsQuery from "lib/queries/AmericanIssuerDividendsQuery";
import GlobalCorporateActionsQuery from "lib/queries/GlobalCorporateActionsQuery";
import type {
  IAmericanIssuerDividendsQuery,
  IAmericanIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/AmericanIssuerDividendsQuery.generated";
import type {
  IGlobalCorporateActionsQuery,
  IGlobalCorporateActionsQueryVariables,
} from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import {
  CalendarDaysIcon,
  CirclePercentIcon,
  HandCoinsIcon,
  SquareSplitHorizontalIcon,
  TicketPercentIcon,
} from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { ISecurityCountry } from "types/graphqlTypes";

interface AmericanEventsBoxProps extends EventsCalendarState {
  tickerSymbol: string;
  isRenderPageDefault?: boolean;
  tab: CompanyEventsTabEnum;
  onChangeTab: (tab: CompanyEventsTabEnum) => void;
}

export default function AmericanEventsBox({
  tickerSymbol,
  isRenderPageDefault = false,
  tab,
  onChangeTab,
  ...filterState
}: AmericanEventsBoxProps) {
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);
  const [enabledFullHeight, setEnabledFullHeight] = useState(false);

  const intl = useIntl();
  const issuer = useAmericanIssuer();
  const viewer = useSelfie();

  const { data: globalCorporateActionsData, loading: globalCorporateActionsLoading } = useQuery<
    IGlobalCorporateActionsQuery,
    IGlobalCorporateActionsQueryVariables
  >(GlobalCorporateActionsQuery, {
    variables: {
      tickerSymbols: issuer.securities.map((node) => node.tickerSymbol),
      securityCountry: ISecurityCountry.Br,
    },
  });

  const {
    data: currentIssuerDividendsData,
    previousData: previousIssuerDividendsData,
    loading: issuerDividendsLoading,
  } = useQuery<IAmericanIssuerDividendsQuery, IAmericanIssuerDividendsQueryVariables>(AmericanIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Us,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  const issuerDividendsData =
    currentIssuerDividendsData && currentIssuerDividendsData ? currentIssuerDividendsData : previousIssuerDividendsData;

  const hasDividends = issuerDividendsData && issuerDividendsData.dividendsPaid.nodes.length > 0;
  const hasStockDividends = issuerDividendsData && issuerDividendsData.stockDividendsPaid.length > 0;
  const hasStockSplit = globalCorporateActionsData && globalCorporateActionsData.stockSplit.length > 0;
  const hasRightsIssues = globalCorporateActionsData && globalCorporateActionsData.rightsIssues.length > 0;

  return (
    <CompanyContainer
      className={classNames("xl:h-fit", {
        "xl:h-full xl:min-h-company-box": enabledFullHeight,
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyEventsTabEnum.DIVIDENDS}
          value={viewer?.isPremium ? tab : CompanyEventsTabEnum.DIVIDENDS}
          onValueChange={(value) => onChangeTab(value as CompanyEventsTabEnum)}
          className="h-auto w-full"
        >
          <CompanyTabsList
            activeTab={tab}
            tabs={[
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.CALENDAR,
                    label: intl.formatMessage({
                      defaultMessage: "Calendário",
                      description: "Aba do menu superior da empresa",
                      id: "GiXunu",
                    }),
                    icon: <CalendarDaysIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              {
                value: CompanyEventsTabEnum.DIVIDENDS,
                label: intl.formatMessage({
                  defaultMessage: "Dividendos",
                  description: "Aba do menu superior da empresa",
                  id: "cesSrh",
                }),
                icon: <HandCoinsIcon className="inline-flex" size={16} strokeWidth={2} />,
                disabled: !hasDividends,
              },
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.STOCK_DIVIDENDS,
                    label: intl.formatMessage({
                      defaultMessage: "Bonificação",
                      description: "Aba do menu superior da empresa",
                      id: "XVUdfX",
                    }),
                    icon: <CirclePercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasStockDividends,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.RIGHTS_ISSUES,
                    label: intl.formatMessage({
                      defaultMessage: "Subscrição",
                      description: "Aba do menu superior da empresa",
                      id: "+HliZT",
                    }),
                    icon: <TicketPercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasRightsIssues,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.STOCK_SPLIT,
                    label: intl.formatMessage({
                      defaultMessage: "Desdobramento",
                      description: "Aba do menu superior da empresa",
                      id: "VZYL3m",
                    }),
                    icon: <SquareSplitHorizontalIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasStockSplit,
                  }
                : null,
            ].filter(notUndefinedOrNull)}
          />

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.CALENDAR}>
              <AmericanCalendarContent
                tickerSymbol={tickerSymbol}
                {...filterState}
                isRenderPageDefault={isRenderPageDefault}
              />
            </CompanyTabsContent>
          )}

          <CompanyTabsContent value={CompanyEventsTabEnum.DIVIDENDS}>
            {issuerDividendsData && issuerDividendsData.dividendsPaid && (
              <AmericanDividendsContent
                tickerSymbol={tickerSymbol}
                loading={issuerDividendsLoading}
                filterDividendsByYear={filterDividendsByYear}
                setFilterDividendsByYear={setFilterDividendsByYear}
                availableYears={issuerDividendsData.dividendsPaid.availableYears}
                data={issuerDividendsData.dividendsPaid.nodes}
                dividendYieldSnapshotEachSymbol={issuerDividendsData.dividendsPaid.dividendYieldByTickerSymbol}
                setEnabledFullHeight={setEnabledFullHeight}
              />
            )}
          </CompanyTabsContent>

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_DIVIDENDS}>
              <AmericanStockDividendsContent
                data={(issuerDividendsData && issuerDividendsData.stockDividendsPaid) || []}
                isRenderPageDefault={isRenderPageDefault}
              />
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.RIGHTS_ISSUES}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <AmericanRightsIssuesContent
                  data={globalCorporateActionsData?.rightsIssues || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_SPLIT}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <AmericanStockSplitContent
                  data={globalCorporateActionsData?.stockSplit || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
